import { DateTime } from 'luxon';
import type {
  DateBound,
  DateFilter,
  DateUnit,
} from '@/components/tree-dnd/tree-dnd-types';

export function resolveTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export function todayIso(): string {
  return DateTime.now().toISODate() ?? '';
}

export function relativeBound(
  offset: number,
  unit: DateUnit,
  timezone: string = resolveTimezone()
): DateBound {
  return { type: 'relative', offset, unit, timezone };
}

export function absoluteBound(date: string): DateBound {
  return { type: 'absolute', date };
}

export const EPOCH_BOUND: DateBound = { type: 'epoch' };

export function isRelativeBound(
  bound: DateBound,
  offset: number,
  unit: DateUnit
): boolean {
  return (
    bound.type === 'relative' && bound.offset === offset && bound.unit === unit
  );
}

export function onSendDayFilter(
  timezone: string = resolveTimezone()
): DateFilter {
  return {
    type: 'window',
    from: relativeBound(0, 'days', timezone),
    to: relativeBound(0, 'days', timezone),
  };
}

export function dayBeforeSendFilter(
  timezone: string = resolveTimezone()
): DateFilter {
  return {
    type: 'window',
    from: relativeBound(1, 'days', timezone),
    to: relativeBound(1, 'days', timezone),
  };
}

export function withinLastFilter(
  offset: number,
  unit: DateUnit,
  timezone: string = resolveTimezone()
): DateFilter {
  return {
    type: 'window',
    from: relativeBound(offset, unit, timezone),
    to: relativeBound(0, 'days', timezone),
  };
}

export function notWithinLastFilter(
  offset: number,
  unit: DateUnit,
  timezone: string = resolveTimezone()
): DateFilter {
  return {
    type: 'window',
    from: EPOCH_BOUND,
    to: relativeBound(offset, unit, timezone),
  };
}

export function defaultRelativeFilter(
  timezone: string = resolveTimezone()
): DateFilter {
  return {
    type: 'relative',
    direction: 'before',
    date: relativeBound(7, 'days', timezone),
  };
}

export function defaultAbsoluteFilter(): DateFilter {
  return {
    type: 'absolute',
    direction: 'after',
    date: todayIso(),
  };
}

export function defaultBetweenFilter(): DateFilter {
  const date = todayIso();
  return {
    type: 'window',
    from: absoluteBound(date),
    to: absoluteBound(date),
  };
}

export type DateFilterMenuSelection =
  | 'anytime'
  | 'onSendDay'
  | 'dayBeforeSend'
  | 'last7Days'
  | 'last30Days'
  | 'withinLast'
  | 'notLast7Days'
  | 'notLast30Days'
  | 'notWithinLast'
  | 'customRelative'
  | 'customAbsolute'
  | 'customWindow';

export function detectDateFilterSelection(
  value: DateFilter
): DateFilterMenuSelection {
  if (value.type === 'anytime') return 'anytime';
  if (value.type === 'relative') return 'customRelative';
  if (value.type === 'absolute') return 'customAbsolute';

  const { from, to } = value;

  if (isRelativeBound(from, 0, 'days') && isRelativeBound(to, 0, 'days')) {
    return 'onSendDay';
  }
  if (isRelativeBound(from, 1, 'days') && isRelativeBound(to, 1, 'days')) {
    return 'dayBeforeSend';
  }
  if (isRelativeBound(from, 7, 'days') && isRelativeBound(to, 0, 'days')) {
    return 'last7Days';
  }
  if (isRelativeBound(from, 30, 'days') && isRelativeBound(to, 0, 'days')) {
    return 'last30Days';
  }
  if (
    from.type === 'relative' &&
    to.type === 'relative' &&
    to.offset === 0 &&
    from.offset > 0
  ) {
    return 'withinLast';
  }
  if (
    from.type === 'epoch' &&
    to.type === 'relative' &&
    to.offset === 7 &&
    to.unit === 'days'
  ) {
    return 'notLast7Days';
  }
  if (
    from.type === 'epoch' &&
    to.type === 'relative' &&
    to.offset === 30 &&
    to.unit === 'days'
  ) {
    return 'notLast30Days';
  }
  if (from.type === 'epoch' && to.type === 'relative' && to.offset > 0) {
    return 'notWithinLast';
  }

  return 'customWindow';
}

export function createFilterForSelection(
  selection: DateFilterMenuSelection,
  previous?: DateFilter
): DateFilter {
  const timezone = resolveTimezone();

  switch (selection) {
    case 'anytime':
      return { type: 'anytime' };
    case 'onSendDay':
      return onSendDayFilter(timezone);
    case 'dayBeforeSend':
      return dayBeforeSendFilter(timezone);
    case 'last7Days':
      return withinLastFilter(7, 'days', timezone);
    case 'last30Days':
      return withinLastFilter(30, 'days', timezone);
    case 'withinLast': {
      if (
        previous?.type === 'window' &&
        previous.from.type === 'relative' &&
        previous.to.type === 'relative' &&
        previous.to.offset === 0 &&
        previous.from.offset > 0 &&
        !(previous.from.offset === 7 && previous.from.unit === 'days') &&
        !(previous.from.offset === 30 && previous.from.unit === 'days')
      ) {
        return withinLastFilter(
          previous.from.offset,
          previous.from.unit,
          timezone
        );
      }
      return withinLastFilter(14, 'days', timezone);
    }
    case 'notLast7Days':
      return notWithinLastFilter(7, 'days', timezone);
    case 'notLast30Days':
      return notWithinLastFilter(30, 'days', timezone);
    case 'notWithinLast': {
      if (
        previous?.type === 'window' &&
        previous.from.type === 'epoch' &&
        previous.to.type === 'relative' &&
        previous.to.offset > 0 &&
        !(previous.to.offset === 7 && previous.to.unit === 'days') &&
        !(previous.to.offset === 30 && previous.to.unit === 'days')
      ) {
        return notWithinLastFilter(
          previous.to.offset,
          previous.to.unit,
          timezone
        );
      }
      return notWithinLastFilter(14, 'days', timezone);
    }
    case 'customRelative':
      return defaultRelativeFilter(timezone);
    case 'customAbsolute':
      return defaultAbsoluteFilter();
    case 'customWindow':
      return defaultBetweenFilter();
  }
}
